import type { RouteLocationRaw } from 'vue-router'
import type { EnrichedSearchResult } from '~/utils/solr/search'
import { normalizeSolrTitleForComparison } from '#shared/utils/solr-title'
import { resolveSolrDocumentUrl } from '~~/shared/utils/solr'
import { hasContentHighlights } from '~/utils/solr/highlighting-utils'

type TextTransform = (value: string | null | undefined) => string

interface SearchTitlePresentation {
  plainText: string
  previewHtml: string
}

const HTML_TITLE_PATTERN = /<|&[a-z#][a-z0-9]*;/i

export function resolveSearchTitlePresentation(
  title: string | null | undefined,
  highlight: string | undefined,
  sanitizeHighlight: TextTransform,
  htmlToText: TextTransform,
): SearchTitlePresentation {
  const decodedTitle = toPlainTitleText(sanitizeHighlight(title), htmlToText)
  const safeTitle = sanitizeHighlight(decodedTitle)
  const plainText = toPlainTitleText(safeTitle, htmlToText)
  const sanitizedHighlight = sanitizeHighlight(highlight)
  const normalizedTitle = normalizeSolrTitleForComparison(plainText)
  const matchesTitle = Boolean(
    sanitizedHighlight &&
    normalizedTitle &&
    normalizeSolrTitleForComparison(toPlainTitleText(sanitizedHighlight, htmlToText)) ===
      normalizedTitle,
  )

  return {
    plainText,
    previewHtml: matchesTitle ? sanitizedHighlight : safeTitle,
  }
}

function toPlainTitleText(value: string, htmlToText: TextTransform): string {
  return HTML_TITLE_PATTERN.test(value) ? htmlToText(value) : value
}

/**
 * Modifies search results with highlighted fields and dynamic URLs.
 * URLs include searchTerm only when match is in bodytext/abstract (not title-only).
 */
export function useHighlightedResults(
  highlighting: ReturnType<typeof useSearchState>['highlighting'],
  searchResult: ReturnType<typeof useSearchState>['searchResult'],
  searchTerm: Ref<string>,
): ComputedRef<EnrichedSearchResult[]> {
  const route = useRoute()
  const { $htmlToText, $stripAnchorTags } = useNuxtApp()

  return computed(() => {
    return searchResult.value.map((doc) => {
      const docHighlight = highlighting.value[doc.id] || {}
      const title = resolveSearchTitlePresentation(
        doc.title,
        docHighlight.title?.[0],
        $stripAnchorTags,
        $htmlToText,
      )

      const includeTerm = route.name === 'search-index' && hasContentHighlights(docHighlight)

      const url: RouteLocationRaw | undefined = resolveSolrDocumentUrl(
        doc,
        includeTerm ? searchTerm.value : undefined,
      )

      return {
        ...doc,
        highlightedTitle: title.plainText,
        highlightedBody:
          (docHighlight.abstract || docHighlight.bodytext)
            ?.map((value) => $stripAnchorTags(value))
            ?.join(' ') || $stripAnchorTags(doc.abstract),
        previewHighlightedTitle: title.previewHtml,
        url,
      }
    })
  })
}
